import React from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "react-toastify";

interface PartnerTermsAcceptedCardProps {
  code: string | null;
  acceptedAt: Date | null;
}

/**
 * The "already accepted" state of the partner terms page: confirmation plus
 * the partner's referral code and shareable link, once the terms are accepted.
 */
const PartnerTermsAcceptedCard: React.FC<PartnerTermsAcceptedCardProps> = ({
  code,
  acceptedAt,
}) => {
  const shareLink = code ? `${window.location.origin}/?ref=${code}` : null;

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  return (
    <div className="with-border mb-8 rounded-2xl bg-white p-4 lg:p-6 dark:bg-primary-10">
      <p className="flex items-center gap-2 font-semibold text-green-700 dark:text-green-400">
        <Check size={18} />
        Terms accepted
        {acceptedAt &&
          ` on ${new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(acceptedAt)}`}
      </p>
      {code && (
        <div className="mt-4">
          <p className="text-sm text-grey-40 dark:text-grey-70">
            Your referral code is{" "}
            <span className="font-mono font-semibold text-grey-10 dark:text-white">{code}</span>.
            Share this link — everyone who signs up through it gets a 90-day free trial:
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="rounded-lg bg-grey-95 px-3 py-2 text-sm text-grey-20 dark:bg-primary-20 dark:text-grey-80">
              {shareLink}
            </code>
            <button
              type="button"
              className="btn btn-secondary flex items-center gap-1.5"
              onClick={copyShareLink}
            >
              <Copy size={16} />
              Copy link
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerTermsAcceptedCard;
